import { useMemo } from 'react';

import {
  type QuickFilterDefinition,
  deleteWhereClause,
} from '@modules/_shared/components/filters/Filters';
import { getValueFromPath } from '@modules/_shared/components/filters/SchemaProvider';
import { useLocale } from '@shared/hooks/locale';

export const usePropertySearchQuickFilters = (params?: {
  disableOfferType?: boolean;
  includeSources?: boolean;
}) => {
  const { t } = useLocale();

  const quickFilters = useMemo<QuickFilterDefinition[]>(
    () => [
      {
        label: t('Created'),
        path: ['property_searches_bool_exp', 'created_at'],
      },
      {
        label: t('Offer type'),
        path: ['property_searches_bool_exp', 'offer_type', '_in'],
        disabled: () => params?.disableOfferType ?? false,
      },
      {
        label: t('Location'),
        path: [
          'property_searches_bool_exp',
          'property_searches_places',
          'place',
        ],
        includeParentsPlaces: true,
        expandSelectionAncestors: true,
      },
      {
        label: t('Max budget'),
        path: ['property_searches_bool_exp', 'computed_budget_lte'],
      },
      {
        label: t('Property type'),
        path: [
          'property_searches_bool_exp',
          'property_types',
          'property_type',
          'composite_type',
          '_in',
        ],
        displayedColumn: 'label',
        filter: {
          path: ['property_type', 'type', '_eq'],
          value: 'composite',
        },
      },
      {
        label: t('Min rooms'),
        path: ['property_searches_bool_exp', 'number_of_rooms_gte'],
      },
      {
        label: t('Contact'),
        path: ['property_searches_bool_exp', 'user'],
      },
      ...(params?.includeSources
        ? [
            {
              label: t('Sources'),
              path: ['property_searches_bool_exp', 'source', '_in'],
              onChange: (value: string[] | undefined, whereClause: {}) => {
                if (value?.length === 1 && value[0] === 'aggregator') {
                  return;
                }

                const newWhere = deleteWhereClause(whereClause, [
                  'property_searches_bool_exp',
                  'aggregator_source',
                  '_in',
                ]);

                return newWhere;
              },
            },
            {
              label: t('Aggregator sources'),
              path: ['property_searches_bool_exp', 'aggregator_source', '_in'],
              disabled: (where: any) => {
                // For now we assume that enum quickFilters always return string array or undefined.
                const value: string[] | undefined = getValueFromPath(
                  ['property_searches_bool_exp', 'source', '_in'],
                  where,
                );

                return value?.length !== 1 || value[0] !== 'aggregator';
              },
            },
          ]
        : []),
      {
        label: t('New / Existing'),
        path: ['property_searches_bool_exp', 'property_status', '_in'],
        isNullable: false,
      },
      {
        label: t('Notification'),
        path: ['property_searches_bool_exp', 'notification', '_in'],
      },
    ],
    [t, params?.disableOfferType, params?.includeSources],
  );

  return quickFilters;
};
